import { memo, useEffect, useState } from "react";
import { connect } from "react-redux";
import GroupSelector from "../../GroupSelector";
import NetworksSelector from "../../NetworksSelector";
import moment from "moment";
import axios from "axios";
import {
  API_URL,
  EXPORT_REPORTS_API_URL,
  FORMAT_DATE_API,
  PAGE_SIZE,
  REPORTS_API_URL,
} from "../../../constants/Globals";
import IntervalSelector from "../../IntervalSelector";
import { numFormatter } from "../../../helpers/Number";
import InfiniteScroll from "react-infinite-scroll-component";

const ArrowIcon = ({ className = "" }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.99778 11.44H6.44222V2.77334L10.4144 6.74556L11.44 5.72L5.72 2.86102e-06L0 5.72L1.02556 6.74556L4.99778 2.77334V11.44Z"
      fill="#2AA974"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.63083 0.815397L4.81543 5.63079L3.26305e-05 0.815397H9.63083Z"
      fill="#828282"
    />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 10 6"
    fill="none"
    style={{ transform: "scaleY(-1)" }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.63083 0.815397L4.81543 5.63079L3.26305e-05 0.815397H9.63083Z"
      fill="#828282"
    />
  </svg>
);

const InfoStatsBox = memo(
  ({ title, slug, intervalDates, selectedNetworks, groupId = "" }) => {
    const [value, setValue] = useState(0);

    useEffect(() => {
      if (intervalDates.start < intervalDates.end) {
        const response = axios
          .get(`${REPORTS_API_URL}/ai-usage/${slug}`, {
            params: {
              groupId: groupId,
              fromDate: intervalDates.start.format(FORMAT_DATE_API),
              toDate: intervalDates.end.format(FORMAT_DATE_API),
              statComparison: 1,
              clientIds: selectedNetworks.join(","),
            },
          })
          .then(({ data }) => {
            setValue(data.data);
          });
      }
    }, [intervalDates, slug, groupId, selectedNetworks]);

    const valueToDisplay = value !== 0 ? numFormatter(value?.totalCount) : 0;

    return (
      <div>
        <div className="info-stat-title">{title}</div>
        <div className="info-stat-value">
          {valueToDisplay}
          <>
            <ArrowIcon
              className={
                parseFloat(value?.statDifference?.totalCount) < 0
                  ? "change-down"
                  : "change-up"
              }
            />
            {/* <span>{Math.abs(changePercent).toFixed(1)}%</span> */}
          </>
        </div>
      </div>
    );
  }
);

const ClientAiUsageTableView = ({
  tableData = [],
  columnTypes = [],
  columnTitles = [],
  additionColumnTypes = [],
  cellTag = () => {},
  hasMoreData = false,
  ageKey = "client-ai-usage",
  sortDirection = "desc",
  sortByColumn = "",
  onSortChange,
  tableRowsTotal = {},
  onBackClick,
  onLoadMore,
  slugify,
  name = "",
}) => {
  const getFromLocalStorage = (key, defaultValue) => {
    const saved = localStorage.getItem(name + "-" + key);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.warn(
          `Error parsing localStorage key "${key}". Returning default value.`
        );
        return defaultValue;
      }
    }
    return defaultValue;
  };

  const setInLocalStorage = (key, value) => {
    localStorage.setItem(name + "-" + key, JSON.stringify(value));
  };

  const getColumnTypeName = (slug) =>
    [...columnTypes, ...additionColumnTypes].find(
      (columnType) => slugify(columnType) === slug
    ) || "";

  const allStatSumCols = [
    "totalTokens",
    "totalAIPollsGenerated",
    "avgTokensPerPoll",
    "estimatedCost",
  ];
  const [selectedColumnTypes, setSelectedColumnTypes] = useState(
    getFromLocalStorage(
      "selectedColumnTypes",
      columnTypes.map((type) => slugify(type))
    )
  );

  const moneyColumns = ["estimatedCost"];
  const percentColumns = [];
  const largeNumberColumns = [];
  const dateColumns = ["date"];
  const numberColumns = ["totalTokens", "totalAIPollsGenerated", "avgTokensPerPoll"];

  function getCellValue(row, selectedColumnType) {
    if (moneyColumns.includes(selectedColumnType)) {
      return "$" + parseFloat(row[slugify(selectedColumnType)]).toFixed(2);
    } else if (percentColumns.includes(selectedColumnType)) {
      return parseFloat(row[slugify(selectedColumnType)]).toFixed(2) + "%";
    } else if (largeNumberColumns.includes(selectedColumnType)) {
      return parseFloat(row[slugify(selectedColumnType)]).toFixed(2);
    } else if (dateColumns.includes(selectedColumnType)) {
      return moment.utc(row[slugify(selectedColumnType)]).format("MMM DD, YYYY");
    } else if (numberColumns.includes(selectedColumnType)) {
      return numFormatter(parseInt(row[slugify(selectedColumnType)]));
    }
    return row[slugify(selectedColumnType)] || "";
  }

  return (
    <div className="table-box">
      <div className="table-box-content">
        <InfiniteScroll
          dataLength={tableData.length} // Length of data loaded so far
          next={onLoadMore} // Function to fetch more data
          hasMore={hasMoreData} // Boolean to indicate if more data can be loaded
          loader={
            <h6 className="txtCenter infinite-loading">Loading more...</h6>
          } // Loader to display while fetching data
        >
          <table cellSpacing="0" cellPadding="0">
            <tbody>
              <tr>
                {selectedColumnTypes.map((selectedColumnType) => (
                  <th
                    key={selectedColumnType}
                    onClick={() => {
                      if (onSortChange) {
                        onSortChange(
                          selectedColumnType,
                          selectedColumnType === sortByColumn &&
                            sortDirection === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }
                    }}
                  >
                    {getColumnTypeName(selectedColumnType)}
                    {sortByColumn === selectedColumnType ? (
                      <>
                        {sortDirection === "desc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )}
                      </>
                    ) : null}
                  </th>
                ))}
              </tr>

              <tr className="totals-row">
                {selectedColumnTypes.map((selectedColumnType, index) => (
                  <td className="txtLeft" key={selectedColumnType}>
                    {allStatSumCols.includes(selectedColumnType)
                      ? getCellValue(
                          tableRowsTotal,
                          slugify(selectedColumnType)
                        )
                      : ""}
                  </td>
                ))}
              </tr>

              {tableData.length > 0 &&
                tableData.map((row, idx) => (
                  <tr key={`${row.date}_${idx}`}>
                    {selectedColumnTypes.map((selectedColumnType, index) => (
                      <th key={selectedColumnType}>
                        <span>{getCellValue(row, selectedColumnType)}</span>
                      </th>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </InfiniteScroll>
      </div>
    </div>
  );
};

function ReportsAiUsage({ allNetworks = [], groups = [] }) {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedNetworks, setSelectedNetworks] = useState([]);
  const [groupNetworks, setGroupNetworks] = useState([]);
  const [intervalDates, setIntervalDates] = useState({
    start: moment().subtract(30, "days"),
    end: moment(),
  });
  const [clientAiUsageTableData, setClientAiUsageTableData] = useState([]);
  const [clientAiUsageRecordTotal, setClientAiUsageRecordTotal] = useState({});
  const [sortByColumnMain, setSortByColumnMain] = useState("date");
  const [sortDirectionMain, setSortDirectionMain] = useState("desc");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);

  /** It will display as tile above table */
  const infoStatsBoxes = [
    { title: "Total Tokens", slug: "totalTokens" },
    // { title: "Total AI Polls Generated", slug: "totalAIPollsGenerated" },
  ];

  useEffect(() => {
    if (selectedGroup) {
      axios
        .get(
          `${API_URL}/admin/groups/getGroupNetworks?groupId=${selectedGroup}&pageNumber=1&perPage=100&order=asc&orderBy=name`
        )
        .then(({ data }) => {
          setGroupNetworks(data.data);
        });
    }
  }, [selectedGroup]);

  const fetchAiUsageListWithAllStatSum = async (loadMore = false, pageNum = 1) => {
    const queryParams = `?sortByColumn=${sortByColumnMain}&sortDirection=${sortDirectionMain}&clientIds=${selectedNetworks.join(",")}&groupId=${selectedGroup || ""}&fromDate=${intervalDates.start.format("YYYY-MM-DD")}&toDate=${intervalDates.end.format("YYYY-MM-DD")}&pageNumber=${pageNum}&perPage=${PAGE_SIZE.INFINITE_SCROLL}`;

    axios.get(`${REPORTS_API_URL}/ai-usage${queryParams}`).then(({ data }) => {

      const newTableData = loadMore
        ? clientAiUsageTableData.concat(data.data.rows || [])
        : data.data.rows || [];
      if (data.data.rows?.length === 0) {
        setHasMoreData(false);
      } else {
        setHasMoreData(
          newTableData.length < Number(data.data.count)
        );
      }

      setClientAiUsageRecordTotal(data.data.allStatsSum);
      setClientAiUsageTableData(newTableData || []);
    });
  };

  useEffect(() => {
    console.log("fetching client ai usage:::::::::::");
    if (intervalDates.start < intervalDates.end) {
      fetchAiUsageListWithAllStatSum();
      setPageNumber(1);
    }
  }, [
    selectedNetworks,
    selectedGroup,
    sortByColumnMain,
    sortDirectionMain,
    intervalDates,
  ]);

  return (
    <>
      <div className="page-headline is-flex">
        <div>
          <strong>Reports</strong>&nbsp;|&nbsp;AI Usage
        </div>
        <IntervalSelector
          startDate={intervalDates.start}
          endDate={intervalDates.end}
          onChange={(startDate, endDate) =>
            setIntervalDates({
              start: startDate,
              end: endDate,
            })
          }
        />
      </div>

      <div className="page-selectors">
        {groups?.length > 0 && (
          <GroupSelector
            value={selectedGroup}
            onChange={(id) => {
              setSelectedGroup(id);
              setSelectedNetworks([]);
              setGroupNetworks([]);
            }}
          />
        )}
        <NetworksSelector
          multiple={true}
          value={selectedNetworks}
          defaultText="All Networks"
          networks={selectedGroup ? groupNetworks : allNetworks}
          onChange={(ids) => setSelectedNetworks(ids)}
        />
      </div>

      <div>
        <div className="info-box">
          <div className="info-box-header">Last 30 days</div>
          <div className="info-box-content">
            <div className="info-stats">
              {infoStatsBoxes.map(({ title, slug }) => (
                <InfoStatsBox
                  intervalDates={intervalDates}
                  selectedNetworks={selectedNetworks}
                  groupId={selectedGroup}
                  title={title}
                  slug={slug}
                  key={slug}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div>&nbsp;</div>
      <div>
        <ClientAiUsageTableView
          name="clientAiUsage"
          dateFormat="MMMM Y"
          tableData={clientAiUsageTableData}
          tableRowsTotal={clientAiUsageRecordTotal}
          ageKey="client-ai-usage"
          sortByColumn={sortByColumnMain}
          sortDirection={sortDirectionMain}
          hasMoreData={hasMoreData}
          columnTypes={[
            "Date",
            "Client Name",
            "Total Tokens",
            "Total AI Polls Generated",
            "Avg Tokens Per Poll",
            "Estimated Cost",
          ]}
          cellTag={() => {}}
          slugify={(name) =>
            name
              .trim()
              .split(" ")
              // .map((piece, index) => (index === 0 ? piece.toLowerCase() : piece))
              .map((piece, index) =>
                index === 0
                  ? piece.charAt(0).toLowerCase() + piece.slice(1)
                  : piece
              )
              .join("")
              .replace("Client Name", "clientName")
              .replace("Date", "date")
              .replace("Total Tokens", "totalTokens")
              .replace("Total AI Polls Generated", "totalAIPollsGenerated")
              .replace("Avg Tokens Per Poll", "avgTokensPerPoll")
              .replace("Estimated Cost", "estimatedCost")
          }
          onSortChange={(column, direction) => {
            setSortByColumnMain(column);
            setSortDirectionMain(direction);
          }}
          onLoadMore={() => {
            const newPage = pageNumber + 1;
            setPageNumber(newPage);
            fetchAiUsageListWithAllStatSum(true, newPage);
          }}
        />
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  allNetworks: state.networks.networksList,
  groups: state.networks.groupsList,
});
// export default ReportsAiUsage;
export default connect(mapStateToProps, {})(ReportsAiUsage);
